import { array, boolean, InferType, mixed, object, string } from 'yup'

import { Premise } from '@/api/models/auth/lib/auth.types'
import {
  DesiredDateType,
  PassAuto,
  PassAutoPayload
} from '@/api/models/requests/lib/requests.types'
import { REQUIRED_FIELD_MESSAGE } from '@/app/constants/validation'
import { isFilesSizeExceeded } from '@/utils/file'

import { FILES_TOTAL_SIZE_EXCEEDED_MESSAGE } from '../../model/constants'
import { ParkingType, PersonalAccountType } from '../../model/form.types'

const commonFields = {
  description: string().max(500, 'Не более 500 символов')
}

const trimValue = (value: string) => {
  return typeof value === 'string' ? value.trim() : value
}

export const generateCommonSchema = () => {
  return object().shape({
    ...commonFields
  })
}

export const generateCommonRequestSchema = () => {
  return object().shape({
    room: mixed<Premise>(),
    personalAccount: mixed<PersonalAccountType>(),
    description: string().transform(trimValue).required(REQUIRED_FIELD_MESSAGE),
    files: array().test(
      'totalSize',
      FILES_TOTAL_SIZE_EXCEEDED_MESSAGE,
      files =>
        files && !isFilesSizeExceeded(files.map(file => file?.file.size || 0))
    ),
    date: mixed(),
    timeSlot: mixed().notRequired(),
    desiredDateType: mixed<DesiredDateType>(),
    desiredDateInterval: mixed()
  })
}

export const generateAutoSchema = (
  isAutoModelRequired = true,
  isParkingRequired = true,
  isNumberRequired = true
) => {
  const numberSchema = string()
    .transform(trimValue)
    .max(20, 'Не более 20 символов')

  return object().shape({
    number: isNumberRequired
      ? numberSchema.required(REQUIRED_FIELD_MESSAGE)
      : numberSchema.optional(),
    model: string()
      [isAutoModelRequired ? 'required' : 'optional'](REQUIRED_FIELD_MESSAGE)
      .transform(trimValue)
      .max(50, 'Не более 50 символов'),
    parkingPlace: mixed<ParkingType>()
      [isParkingRequired ? 'nonNullable' : 'nullable']()
      [isParkingRequired ? 'required' : 'optional'](REQUIRED_FIELD_MESSAGE),
    isStandard: boolean().default(true)
  })
}

export const generateAutosSchema = (
  isAutoModelRequired?: boolean,
  isParkingRequired?: boolean,
  permanent?: boolean,
  isNumberRequired?: boolean
) => {
  return object().shape({
    autos: array()
      .of(
        generateAutoSchema(
          isAutoModelRequired,
          isParkingRequired,
          isNumberRequired
        )
      )
      .required(),
    room: mixed<Premise>(),
    date: string()[permanent ? 'required' : 'optional'](),
    personalAccount: mixed()
  })
}

export const generateAutoPassFormSchema = (permanent = false) =>
  generateAutosSchema(false, false, permanent, false)

export const generatePersonSchema = () => {
  return object().shape({
    value: string().max(55, 'Не более 55 символов').transform(trimValue)
  })
}

export const generatePersonsSchema = (permanent?: boolean) => {
  return object().shape({
    room: mixed<Premise>(),
    persons: array().of(generatePersonSchema()).required(),
    personalAccount: mixed(),
    date: string().nullable(),
    ...(permanent && { date: string() })
  })
}

export const generateAutoPassPropertySchema = (
  isAutoModelRequired: boolean,
  isParkingRequired: boolean,
  isNumberRequired = true
) => {
  const autosSchema = generateAutosSchema(
    isAutoModelRequired,
    isParkingRequired,
    undefined,
    isNumberRequired
  )
  return autosSchema.concat(
    object().shape({
      ...commonFields,
      personalAccount: mixed()
    })
  )
}

export const generateCourierPassSchema = () => {
  return object().shape({
    room: mixed<Premise>(),
    personalAccount: mixed<PersonalAccountType>(),
    date: mixed()
  })
}

export type CommonFormData = InferType<ReturnType<typeof generateCommonSchema>>

export type AutoPassPropertyFormData = InferType<
  ReturnType<typeof generateAutoPassPropertySchema>
>

export type PersonsFormData = InferType<
  ReturnType<typeof generatePersonsSchema>
>
export type PersonsApiData = Omit<PersonsFormData, 'persons'> & {
  description?: string
  persons: string[]
}

export type AutosFormData = InferType<ReturnType<typeof generateAutosSchema>>

export type AutosApiData = Omit<AutosFormData, 'autos'> & {
  autos: PassAutoPayload[]
}

export type CommonRequestFormData = InferType<
  ReturnType<typeof generateCommonRequestSchema>
>

export type CourierPassFormData = InferType<
  ReturnType<typeof generateCourierPassSchema>
>

export type PersonsFormErrors = {
  persons?: { person: string }
}
export type AutosFormErrors = {
  autos?: PassAuto
}
